'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Solicitud } from '@/types/solicitud';
import { clabesService, ClabeListadoItem } from '@/lib/api/clabesService';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { SolicitudDetail } from './SolicitudDetail';
import { SolicitudTable } from './SolicitudTable';
import { useSolicitudFilter, SolicitudFilterUI } from './solicitudFilters';
import { useAuth } from '@/hooks/useAuth';
import ErrorMessage from '@/components/common/ErrorMessage';

const ClabesManagement: React.FC = () => {
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isListadoOpen, setIsListadoOpen] = useState(false);

  const { filtered, baseList, ...filterProps } = useSolicitudFilter(solicitudes);
  const [listado, setListado] = useState<ClabeListadoItem[]>([]);
  const [isLoadingListado, setIsLoadingListado] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState<Solicitud | null>(null);

  useEffect(() => {
    // Obtener ownerID del usuario (sub es el owner_id en el token)
    const ownerID = (user as any)?.sub || (user as any)?.ownerID || '';
    
    if (!ownerID) {
      setIsLoading(false);
      return;
    }
    
    let cancelled = false;
    
    const loadSolicitudes = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Timeout de 15 segundos
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Tiempo de espera agotado')), 15000)
        );
        
        const res = await Promise.race([
          clabesService.getSolicitudes(ownerID),
          timeoutPromise
        ]) as any;
        
        if (cancelled) return;
        
        if (res.data) {
          // Los datos ya vienen en formato correcto del backend
          setSolicitudes(Array.isArray(res.data) ? res.data : []);
        } else {
          setError(res.error || 'Error al cargar solicitudes');
          setSolicitudes([]);
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('Error cargando solicitudes CLABES:', err);
        setError(err.message || 'Error al cargar solicitudes');
        setSolicitudes([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    
    loadSolicitudes();
    
        return () => {
      cancelled = true;
    };
  }, [user?.sub]);

  const handleGenerarListado = async () => {
    const ownerID = (user as any)?.sub || (user as any)?.ownerID || '';
    if (!ownerID) return;
    
    setIsLoadingListado(true);
    setError(null);
    
    const res = await clabesService.getListado(ownerID);
    if (res.data) {
      setListado(res.data);
      setIsListadoOpen(true);
    } else {
      setError(res.error || 'Error al generar listado');
    }
    setIsLoadingListado(false);
  };

  const handleExportarCSV = () => {
    if (listado.length === 0) return;

    const headers = ['Clabe', 'Clave de Rastreo', 'Nombre Beneficiario', 'Referencia Numérica', 'Monto Autorizado', 'Monto Transferencia'];
    const rows = listado.map(item => [
      item.clabe,
      item.claveRastreo,
      item.nombreBeneficiario,
      item.referenciaNumerica,
      item.montoAutorizado?.toFixed(2) || '0.00',
      item.montoTransferencia?.toFixed(2) || '0.00',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Listado_CLABES_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSolicitudClick = (solicitud: Solicitud) => {
    setSelectedSolicitud(solicitud);
    setIsDetailOpen(true);
  };

  const handleUpdateSolicitud = useCallback((updated: Solicitud) => {
    setSolicitudes(prev => prev.map(s => s.id === updated.id ? updated : s));
    if (selectedSolicitud?.id === updated.id) {
      setSelectedSolicitud(updated);
    }
  }, [selectedSolicitud]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CLABES</h1>
          <p className="text-sm text-gray-500 mt-1">
            Validación de CLABE para solicitudes nuevas en &quot;ENTREGA A RIESGOS&quot;
          </p>
        </div>
        <Button 
          onClick={handleGenerarListado}
          disabled={isLoadingListado}
          variant="primary"
        >
          {isLoadingListado ? 'Generando...' : 'Listado CLABES'}
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Filters - mismo que en Solicitudes */}
      <SolicitudFilterUI
        {...filterProps}
        filteredCount={filtered.length}
        baseCount={baseList.length}
        countLabel="solicitudes"
      />

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="text-sm font-semibold text-gray-900">Sin solicitudes</div>
          <div className="mt-1 text-sm text-gray-500">
            No hay solicitudes nuevas con CLABE registrada en estado &quot;ENTREGA A RIESGOS&quot;.
          </div>
        </div>
      ) : (
        <>
          <SolicitudTable
            solicitudes={filtered}
            onEdit={handleSolicitudClick}
            montoColumn="autorizado"
          />
          <div className="text-right text-xs text-gray-400 px-2 mt-2">
            Total: {filtered.length} solicitudes
          </div>
        </>
      )}

      {/* Detail Modal */}
      <SolicitudDetail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        solicitud={selectedSolicitud}
        onUpdate={handleUpdateSolicitud}
        modulo="clabes"
      />

      {/* Listado Modal */}
      <Modal
        isOpen={isListadoOpen}
        onClose={() => setIsListadoOpen(false)}
        confirmOnExit={false}
        title="Listado CLABES - Pendientes de Validación"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleExportarCSV}>
              Exportar CSV
            </Button>
            <Button variant="primary" onClick={() => setIsListadoOpen(false)}>
              Cerrar
            </Button>
          </div>
        }
      >
        <div className="py-4">
          {listado.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No hay CLABEs pendientes de validación.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left p-3 border-r border-gray-200">Clabe</th>
                    <th className="text-left p-3 border-r border-gray-200">Clave de Rastreo</th>
                    <th className="text-left p-3 border-r border-gray-200">Nombre Beneficiario</th>
                    <th className="text-left p-3">Referencia Numérica</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {listado.map((item, idx) => (
                    <tr key={idx} className="hover:bg-blue-50/40 transition-colors">
                      <td className="p-3 border-r border-gray-200 font-mono text-xs">{item.clabe}</td>
                      <td className="p-3 border-r border-gray-200 font-mono text-xs">{item.claveRastreo}</td>
                      <td className="p-3 border-r border-gray-200">{item.nombreBeneficiario}</td>
                      <td className="p-3 font-mono text-xs">{item.referenciaNumerica}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ClabesManagement;
